import { Box, Icon, type IconProps, Badge } from '@customerio/pluma-components/react';
import type { IconName } from '@customerio/pluma-icons';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import * as styles from './health-score-gauge.css';
import { getWorkspaceIdFromCurrentUrl, useCurrentWorkspace } from '@/context/current-workspace';
import { useEnvHealth } from '../api/get-env-health';
import { useWorkspaceHealthSimulation } from '../store';
import { useWorkspaceDetails } from '@/features/masquerade-banner';
import { isEmptyString } from '@/utils/string';
import { getHealthSummary } from '../utils';

const SCORE_COLORS_LARGE = {
  // when the status is healthy, we don't want the arc to be visible
  green: {
    arc: themeVars.palette['green-300'],
    track: themeVars.palette['green-300'],
  },
  yellow: {
    arc: themeVars.color['icon-caution'],
    track: themeVars.color['border-minimal'],
  },
  red: {
    arc: themeVars.color['icon-critical'],
    track: themeVars.color['border-base'],
  },
};

const SCORE_COLORS_SMALL = {
  // when the status is healthy, we don't want the arc to be visible
  green: {
    arc: themeVars.palette['green-300'],
    track: themeVars.palette['green-300'],
  },
  yellow: {
    arc: themeVars.color['icon-caution'],
    track: themeVars.color['border-minimal'],
  },
  red: {
    arc: themeVars.color['icon-critical'],
    track: themeVars.color['border-base'],
  },
};

const SCORE_ICON: Record<string, { name: IconName; color: 'success' | 'caution' | 'critical' }> = {
  green: { name: 'check', color: 'success' },
  yellow: { name: 'caution', color: 'caution' },
  red: { name: 'caution', color: 'critical' },
};

function healthColor(health: string | undefined) {
  switch (health) {
    case 'degraded':
      return 'red';
    case 'busy':
      return 'yellow';
    default:
      return 'green';
  }
}

type HealthIconProps = { iconSize?: IconProps['size']; active?: boolean };

function HealthIcon({ iconSize, active = false }: HealthIconProps) {
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate, active);
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const data = healthQuery.data;
  // Off-page: heavy.data may be stale from a prior unhealthy window. Use the
  // light-derived isUnhealthy flag as the source of truth for the visual state.
  const color = healthQuery.isUnhealthy ? healthColor(data?.health) : healthColor('healthy');

  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;

  const icon = {
    name: isIdentifiersMigrationInProgress ? ('pause' as const) : SCORE_ICON[color].name,
    color: SCORE_ICON[color].color,
  };

  return <Icon name={icon.name} size={iconSize} color={icon.color} />;
}

type HealthLabelProps = { active?: boolean };

export function HealthLabel({ active = false }: HealthLabelProps) {
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate, active);
  const workspaceDetailsQuery = useWorkspaceDetails(workspaceId);
  const data = healthQuery.data;

  const isIdentifiersMigrationInProgress =
    isEmptyString(workspaceDetailsQuery.data?.identifiers_migration_in_progress) === false;
  const { heading } = getHealthSummary(data, isIdentifiersMigrationInProgress);

  // Off-page recovery: ignore stale data.health and render the healthy label.
  if (!healthQuery.isUnhealthy) {
    return <Badge color={healthColor('healthy')}>{heading}</Badge>;
  }

  const color = healthColor(data?.health);
  const isDegraded = data?.health === 'degraded';

  return <Badge color={color}>{isDegraded ? 'Degraded' : heading}</Badge>;
}

type HealthScoreGaugeProps = {
  size?: number;
  stroke?: number;
  showIcon?: boolean;
  iconSize?: IconProps['size'];
  active?: boolean;
};

export function HealthScoreGauge({
  size,
  stroke,
  showIcon = true,
  iconSize = 'lg',
  active = false,
}: HealthScoreGaugeProps) {
  const currentWorkspace = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id ?? getWorkspaceIdFromCurrentUrl() ?? 0;
  const { simulate } = useWorkspaceHealthSimulation();
  const healthQuery = useEnvHealth(workspaceId, simulate, active);

  const data = healthQuery.data;
  // We want to show a score of 80 for degraded and busy in the UI
  // for this component, but the score is still based on the actual score.
  // Off-page: only treat as non-healthy when the fresh light signal agrees,
  // so a stale data.health from a prior unhealthy window doesn't leak through.
  const score = healthQuery.isUnhealthy ? 80 : (data?.score ?? 100);

  const _size = size ?? 84;
  const _stroke = stroke ?? 6;
  const _radius = (_size - _stroke) / 2;
  const _circumference = 2 * Math.PI * _radius;

  const color = healthQuery.isUnhealthy ? healthColor(data?.health) : healthColor('healthy');
  const colorSet = _size < 30 ? SCORE_COLORS_SMALL : SCORE_COLORS_LARGE;
  const colors = colorSet[color];

  // Same arc math for busy and degraded: track + partial stroke (not a full ring for degraded).
  const busyness = 1 - score / 100;
  const dashOffset = _circumference * (1 - busyness);
  const arcColor = colors.arc;
  const trackColor = colors.track;

  const outlineWidth = 1;
  const outerOutlineRadius = _radius + _stroke / 2 + outlineWidth / 2;
  const innerOutlineRadius = _radius - _stroke / 2 - outlineWidth / 2;
  const outlineColor = themeVars.color['icon-base'];

  // When drawing the outer outline, expand viewBox so the stroke isn't clipped
  const svgSize = showIcon ? _size : _size + 2 * outlineWidth;
  const center = svgSize / 2;

  return (
    <Box className={styles.gaugeContainer} style={{ width: _size, height: _size }}>
      <svg width={_size} height={_size} viewBox={`0 0 ${svgSize} ${svgSize}`}>
        {/* Outer outline (only when icon is hidden) */}
        {!showIcon ? (
          <circle
            cx={center}
            cy={center}
            r={outerOutlineRadius}
            fill="none"
            stroke={outlineColor}
            strokeWidth={outlineWidth}
          />
        ) : null}
        {/* Track circle */}
        <circle
          className={styles.gaugeTrack}
          cx={center}
          cy={center}
          r={_radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={_stroke}
        />
        {/* Progress arc */}
        <circle
          className={styles.gaugeArc}
          cx={center}
          cy={center}
          r={_radius}
          fill="none"
          stroke={arcColor}
          strokeWidth={_stroke}
          strokeLinecap="round"
          strokeDasharray={_circumference}
          strokeDashoffset={dashOffset}
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Inner outline */}
        {!showIcon ? (
          <circle
            cx={center}
            cy={center}
            r={innerOutlineRadius}
            fill="none"
            stroke={outlineColor}
            strokeWidth={outlineWidth}
          />
        ) : null}
      </svg>
      {showIcon ? (
        <Box className={styles.gaugeLabel}>
          <HealthIcon iconSize={iconSize} active={active} />
        </Box>
      ) : null}
    </Box>
  );
}
